import React, { useEffect, useState } from 'react';
import {
    Order,
    OrderItem,
    useAddItemToCartMutation,
    useCreateCheckoutMutation,
    useDutchieStore,
    useGetCustomers,
    useI18n,
    useSpringBigStore,
    useTheme,
    useUpdateQuantityCartMutation,
} from '@springbig/shared';
import {
    VStack,
    HStack,
    Image,
    Box,
} from 'native-base';
import { Caption, H1, H2 } from '~/shared/Typography';
import { format } from 'date-fns';
import { TabSelector } from '~/pages/shop/components/TabSelector';
import { OrderDetailPage } from '~/pages/shop/OrderDetailPage';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { useAppNavigate } from '..';
import { useToast } from '~/hooks/toast';
import {
    AddItemToCartResponse,
    CreateCheckoutResponse,
    UpdateQuantityCartResponse
} from '@springbig/shared/src/services/dutchie/mutations';
import { Section, Button, Card, ActionSheet } from '~/shared/Theme';

export const OrdersPage = () => {

    const {
        radius_md,
        highlight_text,
        primary,
    } = useTheme();

    const { t } = useI18n();
    const { member, walletId } = useSpringBigStore();
    const { params, navigate } = useAppNavigate();

    const [showEmailVerfication, setShowEmailVerfication] =
        useState(false);

    const [selectedOrder, setSelectedOrder] =
        useState<Order | null>();

    const {
        orders,
        retailer,
        setOrders,
        menuType,
        checkout,
        fullfillmentOption,
        setCheckout,
    } = useDutchieStore();

    const { mutate: getCustomers } = useGetCustomers();

    type Status = 'open' | 'closed';

    const tabs = [
        {
            name: 'current cart',
            action: () => navigate(`w/${params.wallet_id}/shop/cart`),
        },
        {
            name: 'past orders',
            isSelected: true,
        },
    ];

    useEffect(() => {

        if (orders?.length === 0 && member && member?.consumer.email_validated && member?.consumer.email) {
            getCustomers({
                'retailerId': retailer?.id,
                'filters': { 'email': member?.consumer.email.toLocaleLowerCase() }
            }, {
                onSuccess(data) {

                    const firstFive = data.customers.splice(0, 4);
                    const firstFiveOrders: Array<Order> = [];
                    firstFive.forEach((customer) => {

                        customer.orders?.forEach((order) => {

                            firstFiveOrders.push(order);
                        });
                    });
                    firstFiveOrders.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));
                    if (firstFiveOrders) {
                        setOrders(firstFiveOrders);
                    }
                },
            });
        }
    }, [member, orders, retailer?.id, getCustomers, setOrders]);

    if (!member?.consumer.email_validated) {
        return (
            <Section p={4}>
                <TabSelector tabs={tabs} />
                <VStack
                    h={'100%'}
                    w={'100%'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    space={4}
                >
                    <Caption text={'shop.oops'} />
                    <Caption text={'shop.we_cant_find'} />
                    <Caption text={'shop.add_your_email'} />
                    <Button
                        w={'full'}
                        text={'shop.add_email'}
                        onPress={() => setShowEmailVerfication(true)}
                    />

                    <ActionSheet
                        isOpen={showEmailVerfication}
                        onClose={() => setShowEmailVerfication(false)}
                    >
                        <Box>
                            <H2
                                text={'shop.update_your_email'}
                                textAlign={'center'}
                            />
                            { walletId &&
                                    <EmailVerificationForm
                                        onCancel={() => setShowEmailVerfication(false)}
                                        onProceed={() => setShowEmailVerfication(false)}
                                        t={t}
                                        walletId={walletId}
                                        emailVerificationFormType='ordersPage'
                                    />
                            }
                        </Box>
                    </ActionSheet>
                </VStack>
            </Section>
        );
    }

    const { mutate: addItem } = useAddItemToCartMutation();
    const { mutate: createCheckout } = useCreateCheckoutMutation();
    const { mutate: updateQuantity } = useUpdateQuantityCartMutation();
    const { errorToast } = useToast();

    const onReOrderError = (error, orderItem: OrderItem) => {

        if (error.data.errors[0].message === 'Not Found') {
            createCheckout({
                pricingType: menuType,
                orderType: fullfillmentOption?.key,
                retailerId: retailer?.id,
            });
        }
        else if (error.data.errors[0].message === 'Validation errors') {
            updateQuantity({
                retailerId: retailer?.id,
                checkoutId: checkout?.id,
                itemId: checkout?.items.find((e) => e.product.id === orderItem.product?.id)?.id,
                quantity: orderItem.quantity,
            }, {

                onSuccess(addItemData: UpdateQuantityCartResponse) {

                    setCheckout(addItemData.updateQuantity);
                    navigate(`w/${params.wallet_id}/shop/cart`);
                },
                onError: (errorUpdate) => {

                    if (errorUpdate.data.errors[0].message === 'Not Found') {
                        createCheckout({
                            pricingType: menuType,
                            orderType: fullfillmentOption?.key,
                            retailerId: retailer?.id,
                        });
                    }
                    else {
                        errorToast(errorUpdate.data.errors[0].message);
                    }
                }
            });
        }
        else {
            errorToast(error.data.errors[0].message);
        }
    };

    const reorder = (order: Order) => {

        order.items.forEach((orderItem: OrderItem) => {

            if (checkout) {
                addItem({
                    retailerId: retailer?.id,
                    checkoutId: checkout?.id,
                    quantity: orderItem.quantity,
                    option: orderItem.option,
                    productId: orderItem.product?.id
                }, {
                    onSuccess(data: AddItemToCartResponse) {

                        setCheckout(data.addItem);
                        navigate(`w/${params.wallet_id}/shop/cart`);
                    },
                    onError: (error) => {

                        onReOrderError(error, orderItem);
                    }
                });
            }
            else {
                createCheckout({
                    pricingType: menuType,
                    orderType: fullfillmentOption?.key,
                    retailerId: retailer?.id,
                }, {
                    onSuccess(data: CreateCheckoutResponse) {

                        addItem({
                            retailerId: retailer?.id,
                            checkoutId: data.createCheckout?.id,
                            quantity: orderItem.quantity,
                            option: orderItem.option,
                            productId: orderItem.product?.id
                        }, {
                            onSuccess(addItemData: AddItemToCartResponse) {

                                setCheckout(addItemData.addItem);
                                // onClose();
                            },
                            onError: (error) => {

                                onReOrderError(error, orderItem);
                            }
                        });
                    },
                });
            }
        });
    };

    return ( orders ?
        selectedOrder ?
            <OrderDetailPage
                order={selectedOrder}
                onCancel={() => setSelectedOrder(null)}
            />
            :
            <Section p={4}>
                <TabSelector tabs={tabs} />
                <VStack space={4}>
                    <H1
                        text={'shop.your_orders'}
                        alignSelf={'center'}
                    />
                    { orders.length === 0 ?
                        <Caption alignSelf={'center'}>
                            no previous orders
                        </Caption>
                        :
                        orders.map((order, index) =>

                            <Card
                                row
                                key={index}
                                space={2}
                                p={2}
                                justifyContent={'space-between'}
                            >
                                <Image
                                    key={index}
                                    src={order?.items[0]?.product?.image + '?w=100&h=100'}
                                    rounded={radius_md && radius_md / 2 || 0}
                                    w={'100px'}
                                    h={'100px'}
                                />
                                <VStack flex={1} space={1} alignItems={'center'}>
                                    <Caption color={highlight_text}>
                                        order status: { (order.status as Status) === 'open' ? 'open' : 'completed' }
                                    </Caption>
                                    <Caption>
                                        { format(new Date(order.createdAt), 'EEEE, LLLL d') }
                                    </Caption>
                                    <HStack space={2} alignItems={'center'}>
                                        <Caption>
                                            { order.items.length } { t('shop.item') }{ order.items.length > 1 ? t('shop.s') : '' }
                                        </Caption>
                                        <Box bg={primary} w={1} h={1} rounded={'full'}></Box>
                                        <Caption>${ order.total.toFixed(2) }</Caption>
                                    </HStack>
                                    <HStack space={2} flexWrap={'wrap'}>
                                        <Button
                                            text={'shop.reorder'}
                                            modifier={'small'}
                                            px={2}
                                            onPress={() => reorder(order)}
                                        />
                                        <Button
                                            text={'shop.view_details'}
                                            modifier={'small'}
                                            px={2}
                                            onPress={() => setSelectedOrder(order)}
                                        />
                                    </HStack>
                                </VStack>
                            </Card>
                        )}
                </VStack>
            </Section>
        : <></>
    );
};
